// React Training Page with Sidebar Navigation + Text-to-Speech Explanation + GPT Chat Support + Practice + Score Gating
// Custom CSS version (uses Training.css)

import React, { useState } from "react";
import axios from "axios";
import "./Training.css";
import trainingData from "./Training.json";

export default function TrainingPage() {
    const [activeId, setActiveId] = useState(trainingData[0].id);
    const [showTest, setShowTest] = useState(false);
    const [chatHistory, setChatHistory] = useState([]);
    const [userInput, setUserInput] = useState("");
    const [practiceAnswers, setPracticeAnswers] = useState({});
    const [scores, setScores] = useState({});

    const activeModule = trainingData.find((mod) => mod.id === activeId);
    const activeIndex = trainingData.findIndex((mod) => mod.id === activeId);

    const handleNext = () => {
        const currentScore = scores[activeId] || 0;
        if (currentScore < 5) {
            alert("⚠️ Score too low! Please re-practice before moving forward.");
            return;
        }
        if (activeIndex + 1 < trainingData.length) {
            setActiveId(trainingData[activeIndex + 1].id);
            if ((activeIndex + 1) % 5 === 0) {
                setShowTest(true);
            }
        }
    };

    const speakText = (text) => {
        const utterance = new SpeechSynthesisUtterance(text);
        speechSynthesis.speak(utterance);
    };

    const handleChatSubmit = async () => {
        if (!userInput.trim()) return;
        const newEntry = { role: "user", text: userInput };
        setChatHistory([...chatHistory, newEntry]);
        setUserInput("");

        try {
            const response = await axios.post(
                "https://api.openai.com/v1/chat/completions",
                {
                    model: "gpt-3.5-turbo",
                    messages: [
                        { role: "system", content: "You are a helpful ERP training assistant for the Auditusierp software." },
                        ...chatHistory.map(entry => ({
                            role: entry.role === "user" ? "user" : "assistant",
                            content: entry.text,
                        })),
                        { role: "user", content: userInput }
                    ],
                },
                {
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer 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`
                    },
                }
            );

            const replyText = response.data.choices[0].message.content;
            setChatHistory(prev => [...prev, { role: "bot", text: replyText }]);
        } catch (err) {
            setChatHistory(prev => [...prev, { role: "bot", text: "❌ Error getting AI reply." }]);
        }
    };

    const handlePracticeChange = (e) => {
        setPracticeAnswers({ ...practiceAnswers, [activeId]: e.target.value });
    };

    const handlePracticeSubmit = () => {
        const answer = practiceAnswers[activeId] || "";
        const expected = activeModule.practiceAnswer?.toLowerCase() || "";
        const score = answer.trim().toLowerCase() === expected ? 10 : 4;
        setScores({ ...scores, [activeId]: score });
        alert(`✅ Practice submitted! You scored ${score}/10.`);
    };

    const isModuleLocked = (id) => {
        const index = trainingData.findIndex(mod => mod.id === id);
        for (let i = 0; i < index; i++) {
            const modId = trainingData[i].id;
            if ((scores[modId] || 0) < 5) return true;
        }
        return false;
    };

    return (
        <div className="training-container">
            {/* Sidebar */}
            <aside className="training-sidebar">
                <div className="sidebar-header">Auditusierp Training</div>
                <ul className="sidebar-list">
                    {trainingData.map((item, index) => (
                        <li
                            key={item.id}
                            className={`sidebar-item ${activeId === item.id ? "active" : ""} ${isModuleLocked(item.id) && item.id !== activeId ? "disabled" : ""
                                }`}
                            onClick={() => {
                                if (!isModuleLocked(item.id) || item.id === activeId) {
                                    setActiveId(item.id);
                                    setShowTest(false);
                                } else {
                                    alert("🚫 Please complete and pass the previous module first.");
                                }
                            }}
                        >
                            {index + 1}. {item.title}
                        </li>
                    ))}
                </ul>
            </aside>

            {/* Main Content */}
            <main className="training-main">
                <div className="training-card">
                    <h2 className="training-title">{activeModule.title}</h2>

                    {/* Learning Instructions */}
                    <div className="learning-guide">
                        <h3>📘 How to Learn This Module</h3>
                        <ul>
                            <li>▶ Watch the video below carefully</li>
                            <li>📝 Read the text and notes after the video</li>
                            <li>🎯 Fill out the practice form below the video</li>
                            <li>🔊 Click the "Listen to Explanation" for audio guidance</li>
                            <li>➡ You can move next only if you score at least 5</li>
                        </ul>
                    </div>

                    <div className="video-container">
                        <iframe
                            src={activeModule.videoUrl}
                            title={activeModule.title}
                            frameBorder="0"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowFullScreen
                        ></iframe>
                    </div>

                    <p className="training-description">{activeModule.description}</p>

                    <div className="training-note">
                        <strong>Note:</strong> {activeModule.note}
                    </div>

                    {/* Practice Section */}
                    <div className="practice-form">
                        <h4>🧪 Practice Activity</h4>
                        <p>Try practicing by entering your answer:</p>
                        <input
                            type="text"
                            value={practiceAnswers[activeId] || ""}
                            onChange={handlePracticeChange}
                            placeholder="Type your answer here"
                            className="practice-input"
                            disabled={scores[activeId] >= 5}
                        />
                        <button className="training-button" onClick={handlePracticeSubmit} disabled={scores[activeId] >= 5}>
                            Submit Practice
                        </button>
                    </div>

                    {/* Buttons Row */}
                    <div className="training-controls">
                        {activeModule.speech && (
                            <button
                                onClick={() => speakText(activeModule.speech)}
                                className="training-button"
                            >
                                🔊 Listen to Explanation
                            </button>
                        )}

                        {activeIndex + 1 < trainingData.length && (
                            <button onClick={handleNext} className="training-button">
                                ➡ Next Module
                            </button>
                        )}
                    </div>

                    {showTest && (
                        <div className="test-popup">
                            <h3 className="test-title">📝 Module Test</h3>
                            <p>You’ve completed 5 modules! Click below to take the quiz.</p>
                            <button
                                onClick={() => alert("Redirect to Quiz Page")}
                                className="test-button"
                            >
                                Start Quiz
                            </button>
                        </div>
                    )}

                    {/* Chat Support */}
                    <div className="training-chat">
                        <h4>💬 Ask a Question About This Module</h4>
                        <div className="chat-history">
                            {chatHistory.map((entry, index) => (
                                <div
                                    key={index}
                                    className={`chat-bubble ${entry.role === "user" ? "user" : "bot"}`}
                                >
                                    {entry.text}
                                </div>
                            ))}
                        </div>
                        <div className="chat-input-wrapper">
                            <input
                                value={userInput}
                                onChange={(e) => setUserInput(e.target.value)}
                                className="chat-input"
                                placeholder="Type your question..."
                            />
                            <button onClick={handleChatSubmit} className="training-button">
                                Ask
                            </button>
                        </div>
                    </div>
                </div>
            </main>
        </div>
    );
}
